import { useState, useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { 
  Menu, 
  X, 
  Check, 
  ChevronLeft, 
  ChevronRight, 
  Clock, 
  Calendar,
  ArrowRight,
  Linkedin,
  Instagram,
  ExternalLink,
  Play,
  Youtube,
  Mail,
  Download,
  FileText,
  Mic,
  Presentation,
  Facebook,
  Link2,
  Loader2,
  Sparkles
} from "lucide-react";
import { Button } from "@/components/ui/button";
import AdminDashboard from "@/pages/AdminDashboard";

// Logo URLs
const LOGO_URL = "https://customer-assets.emergentagent.com/job_5d03a66d-48ea-432c-873f-a6fcec69fb36/artifacts/oq0ykvkf_Redefining%20Return%20Logo%20%281%29.png";
const COACH_1_IMG = "https://customer-assets.emergentagent.com/job_leadership-architect/artifacts/wq9pnbpz_ChereeRR.png";
const COACH_2_IMG = "https://customer-assets.emergentagent.com/job_leadership-architect/artifacts/kwjcgfr9_EraniaRR.png";

const HASWELL_CO = "https://www.haswellandco.com";

// API URL
const API_URL = process.env.REACT_APP_BACKEND_URL;

// External Links
const YOUTUBE_CHANNEL = "https://www.youtube.com/@RedefiningReturnPodcast";
const CALENDLY_ERANIA = "https://calendly.com/erania/redefining-return-executive-coaching-interest-session";
const CALENDLY_CHEREE = "https://calendly.com/haswellandco";
const BRACKETT_AGENCY = "https://www.brackett.agency";

// Enso Circle Component - Uses the official logo with background matching
const EnsoCircle = ({ className = "", size = 400 }) => (
  <div 
    className={`relative overflow-hidden ${className}`}
    style={{ 
      width: size, 
      height: size,
      backgroundColor: '#F5F1E6'
    }}
  >
    <img 
      src={LOGO_URL}
      alt="Enso Circle"
      className="absolute"
      style={{ 
        width: '100%',
        height: 'auto',
        top: '-5%',
        left: '0%',
        objectFit: 'contain',
        objectPosition: 'center top',
        clipPath: 'inset(0 0 25% 0)',
        mixBlendMode: 'darken'
      }}
    />
  </div>
);

// Navigation Component
const Navigation = ({ scrolled, activeSection }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const navLinks = [
    { href: "#home", label: "Home" },
    { href: "#coaching", label: "Coaching" },
    { href: "#approach", label: "Approach" },
    { href: "#team", label: "Coaches" },
    { href: "#podcast", label: "Podcast" },
    { href: "/press", label: "Press", isPage: true },
  ];

  const scrollToSection = (href) => {
    setMobileMenuOpen(false);
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleNavClick = (e, link) => {
    e.preventDefault();
    setMobileMenuOpen(false);
    if (link.isPage) {
      navigate(link.href);
    } else {
      if (location.pathname !== '/') {
        navigate('/');
        setTimeout(() => {
          const element = document.querySelector(link.href);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);
      } else {
        scrollToSection(link.href);
      }
    }
  };

  return (
    <>
      <nav className={`navbar glass-nav ${scrolled ? 'scrolled border-b border-black/5' : ''}`} data-testid="main-navigation">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-20">
            <a href="/" onClick={(e) => { e.preventDefault(); navigate('/'); }} data-testid="logo-link">
              <img 
                src={LOGO_URL} 
                alt="Redefining Return" 
                className="h-14 w-auto object-contain"
                style={{ mixBlendMode: 'darken' }}
              />
            </a>
            
            <ul className="hidden lg:flex items-center gap-10">
              {navLinks.map((link) => (
                <li key={link.href}>
                  <a
                    href={link.href}
                    onClick={(e) => handleNavClick(e, link)}
                    className={`text-sm tracking-wide font-medium transition-colors ${
                      (link.isPage && location.pathname === link.href) || (!link.isPage && activeSection === link.href.slice(1)) 
                        ? 'text-[#C27C62]' 
                        : 'text-black/70 hover:text-[#C27C62]'
                    }`}
                    data-testid={`nav-link-${link.isPage ? link.href.slice(1) : link.href.slice(1)}`}
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>

            <button
              className="block lg:hidden p-2 z-50"
              onClick={() => setMobileMenuOpen(true)}
              aria-label="Open menu"
              data-testid="mobile-menu-toggle"
            >
              <Menu className="w-6 h-6 text-black/80" />
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="mobile-menu" data-testid="mobile-menu">
          <button
            className="absolute top-6 right-6 p-2"
            onClick={() => setMobileMenuOpen(false)}
            aria-label="Close menu"
            data-testid="mobile-menu-close"
          >
            <X className="w-8 h-8 text-black/70" />
          </button>
          {navLinks.map((link, i) => (
            <a
              key={link.href}
              href={link.href}
              onClick={(e) => handleNavClick(e, link)}
              className="mobile-menu-link animate-fade-in-up"
              style={{ animationDelay: `${i * 100}ms` }}
              data-testid={`mobile-nav-link-${link.isPage ? link.href.slice(1) : link.href.slice(1)}`}
            >
              {link.label}
            </a>
          ))}
        </div>
      )}
    </>
  );
};

// Hero Section - Editorial Asymmetric Layout
const HeroSection = () => {
  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="home" className="hero-section" data-testid="hero-section">
      {/* Decorative circles */}
      <div className="deco-circle w-[600px] h-[600px] -top-40 -right-40 opacity-30" />
      <div className="deco-circle w-[400px] h-[400px] bottom-20 -left-20 opacity-20" />
      
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-8 lg:py-12 relative">
        {/* Enso Circle - positioned absolutely on right with animation */}
        <div className="hidden lg:block absolute right-12 top-6 enso-animate">
          <EnsoCircle size={480} className="opacity-90" />
        </div>
        
        {/* Hero Content - left aligned, larger and more impactful */}
        <div className="lg:max-w-2xl">
          <p className="text-sm tracking-[0.4em] uppercase text-[#5A6F8C] font-semibold mb-4">
            Executive Coaching
          </p>
          <h1 className="text-6xl md:text-7xl lg:text-8xl font-light tracking-tight leading-[1.05] mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            <em className="text-[#C27C62]">Redefining</em>
            <br />
            Your Return
          </h1>
          <p className="text-xl text-black/60 font-light leading-relaxed max-w-xl">
            The Enso circle symbolizes <em className="text-[#C27C62]">transformation</em> — 
            the moment when the mind is free and the body creates. 
            We guide senior leaders through their most meaningful transitions.
          </p>
        </div>
      </div>
    </section>
  );
};

// Services Section - Editorial Cards
const ServicesSection = () => {
  const services = [
    {
      title: "Career Transition",
      subtitle: "Coaching",
      description: "Navigate your return to executive leadership after a career break, pivot, or significant life change with clarity and confidence.",
      features: [
        "Career positioning & market alignment",
        "Personal brand development",
        "Network reactivation strategies",
        "Interview preparation & negotiation",
        "Personalized action playbook"
      ],
      accent: "terracotta"
    },
    {
      title: "Executive Leadership",
      subtitle: "Coaching",
      description: "Elevate your leadership presence and effectiveness as you step into new or expanded executive roles with authenticity.",
      features: [
        "Leadership presence & authenticity",
        "Strategic thinking frameworks",
        "Stakeholder relationship building",
        "Team development & culture",
        "Personalized action playbook"
      ],
      accent: "sage"
    },
    {
      title: "Strategic Career",
      subtitle: "Mapping",
      description: "Design a career roadmap that aligns with your values, strengths, and life priorities while positioning you for opportunity.",
      features: [
        "Values & strengths assessment",
        "Market opportunity analysis",
        "Personal career blueprint",
        "Long-term vision planning"
      ],
      accent: "indigo"
    }
  ];

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="coaching" className="section-beige py-8 md:py-10" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Section Header - Left aligned */}
        <div className="max-w-2xl mb-10">
          <p className="text-sm tracking-[0.3em] uppercase text-[#94A48A] font-medium mb-4">
            Our Services
          </p>
          <h2 className="section-title mb-6">
            Coaching for <em>Every</em> Chapter
          </h2>
          <p className="section-subtitle">
            Strategic guidance for executives architecting their leadership legacy with confidence and clarity.
          </p>
        </div>

        {/* Editorial Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <div
              key={index}
              className={`editorial-card hover-lift service-card-${service.accent}`}
              data-testid={`service-card-${index}`}
            >
              <div className="mb-6">
                <h3 className="text-2xl font-light text-black/90 mb-1" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  {service.title}
                </h3>
                <span className="text-lg italic text-[#C27C62]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  {service.subtitle}
                </span>
              </div>
              
              <p className="text-black/60 text-sm leading-relaxed mb-8">
                {service.description}
              </p>
              
              <ul className="space-y-3">
                {service.features.map((feature, fIndex) => (
                  <li key={fIndex} className="feature-item text-sm">
                    <Check className="feature-icon w-4 h-4" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Approach Section - Timeline Style
const ApproachSection = () => {
  const steps = [
    {
      number: "01",
      title: "Discovery",
      description: "We begin by understanding your unique situation, goals, strengths, and challenges through deep conversations and structured assessments.",
      color: "#C27C62"
    },
    {
      number: "02",
      title: "Strategy",
      description: "Together, we design a customized roadmap aligned with your values and career aspirations, including positioning strategies and frameworks.",
      color: "#94A48A"
    },
    {
      number: "03",
      title: "Action",
      description: "We support you through implementation with regular coaching sessions, practical tools, and ongoing accountability to build momentum.",
      color: "#5A6F8C"
    },
    {
      number: "04",
      title: "Sustain",
      description: "Our goal is lasting transformation. We help you develop the mindsets and systems needed to thrive and continue evolving as a leader.",
      color: "#D4B06C"
    }
  ];

  return (
    <section id="approach" className="section-cream py-10 md:py-14" data-testid="approach-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Left - Header */}
          <div className="lg:col-span-4">
            <p className="text-sm tracking-[0.3em] uppercase text-[#C27C62] font-medium mb-4">
              Our Approach
            </p>
            <h2 className="section-title mb-6">
              A Path of <em>Intention</em>
            </h2>
            <p className="section-subtitle mb-8">
              Like the Enso, our process is circular — always returning, always evolving. Four stages guide your transformation.
            </p>
            <div className="hidden lg:block">
              <EnsoCircle size={200} className="opacity-40" />
            </div>
          </div>
          
          {/* Right - Steps */}
          <div className="lg:col-span-8">
            <div className="relative pl-8 lg:pl-12">
              <div className="approach-line" />
              
              <div className="space-y-12">
                {steps.map((step, index) => (
                  <div key={index} className="relative" data-testid={`approach-step-${index}`}>
                    <div 
                      className="approach-dot absolute -left-8 lg:-left-12"
                      style={{ borderColor: step.color }}
                    />
                    <div className="pl-4">
                      <div className="flex items-baseline gap-4 mb-3">
                        <span className="text-5xl font-light" style={{ fontFamily: 'Cormorant Garamond, serif', color: step.color }}>
                          {step.number}
                        </span>
                        <h3 className="text-2xl font-light text-black/90" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                          {step.title}
                        </h3>
                      </div>
                      <p className="text-black/60 leading-relaxed max-w-lg">
                        {step.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Stats Section
const StatsSection = () => {
  const [counts, setCounts] = useState({ leaders: 0, years: 0, pl: 0 });
  const [hasAnimated, setHasAnimated] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          animateCounters();
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, [hasAnimated]);

  const animateCounters = () => {
    const targets = { leaders: 150, years: 40, pl: 500 };
    const duration = 2500;
    const steps = 80;
    const stepDuration = duration / steps;

    let currentStep = 0;
    const interval = setInterval(() => {
      currentStep++;
      const progress = currentStep / steps;
      const easeOut = 1 - Math.pow(1 - progress, 4);

      setCounts({
        leaders: Math.round(targets.leaders * easeOut),
        years: Math.round(targets.years * easeOut),
        pl: Math.round(targets.pl * easeOut)
      });

      if (currentStep >= steps) {
        clearInterval(interval);
      }
    }, stepDuration);
  };

  return (
    <section ref={sectionRef} className="stats-section" data-testid="stats-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
          <div className="stat-item" data-testid="stat-leaders">
            <div className="stat-number">{counts.leaders}+</div>
            <div className="stat-label">Senior Leaders Coached</div>
          </div>
          <div className="stat-item" data-testid="stat-years">
            <div className="stat-number">{counts.years}+</div>
            <div className="stat-label">Years Executive Experience</div>
          </div>
          <div className="stat-item" data-testid="stat-pl">
            <div className="stat-number">${counts.pl}M+</div>
            <div className="stat-label">P&L Leadership</div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Leaders We Serve Section
const LeadersServeSection = () => {
  const industries = [
    "HEALTHCARE", "TECHNOLOGY", "MANUFACTURING", "CONSUMER GOODS",
    "FINANCIAL SERVICES", "LIFE SCIENCES", "INDUSTRIAL"
  ];

  return (
    <section className="section-cream py-8" data-testid="leaders-serve-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 mb-8">
        <div className="text-center">
          <p className="text-sm tracking-[0.3em] uppercase text-[#5A6F8C] font-medium mb-4">
            Leaders We Serve
          </p>
          <h2 className="section-title">
            Across <em>Industries</em>
          </h2>
        </div>
      </div>
      
      <div className="ticker-container">
        <div className="ticker-track flex">
          {[...industries, ...industries, ...industries].map((industry, index) => (
            <span key={index} className="ticker-item">
              {industry}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
};

// Team Section - Editorial Layout
const TeamSection = () => {
  const coaches = [
    {
      name: "Chereé Johnson",
      title: "Former CLO & General Counsel",
      subtitle: "Board Advisor • Executive Coach",
      image: COACH_1_IMG,
      bio: "Award-winning C-suite executive and trusted strategic advisor to Boards of Directors across publicly traded and privately held companies spanning consumer, manufacturing, life sciences, medical technology, and industrial sectors.",
      bio2: "She specializes in helping senior leaders develop authentic leadership presence, strategic resilience, and the executive gravitas needed to architect their legacy and navigate board-level challenges.",
      credentials: "Bachelor of Science, Chemical Engineering • Juris Doctor",
      agency: {
        name: "Haswell & Co.",
        url: HASWELL_CO,
        description: "AI governance and executive coaching for leaders navigating complexity. Creator of The Velocity Protocol™ — delivering board-ready AI governance frameworks in 6-8 weeks."
      },
      contactEmail: null,
      calendlyLink: CALENDLY_CHEREE
    },
    {
      name: "Erania Brackett",
      title: "Former CMO & SVP",
      subtitle: "Former Board Director • Executive Coach",
      image: COACH_2_IMG,
      bio: "Seasoned executive with extensive C-suite leadership across publicly traded companies in healthcare, technology, and consumer sectors. Her strategic expertise spans digital marketing transformation, brand strategy development, and major business integrations.",
      bio2: "She specializes in helping executives craft compelling personal brands, architect their leadership legacy, and build the executive presence needed to secure senior director, VP, and C-level opportunities.",
      credentials: "BS Chemical Engineering • Executive MBA",
      agency: {
        name: "Brackett Agency",
        url: BRACKETT_AGENCY,
        description: "Fractional CSO & CMO services powered by Stanford-born predictive intelligence. Delivering data-driven brand and marketing strategies that help leaders navigate complexity, mitigate risk, and accelerate sustainable growth."
      },
      calendlyLink: CALENDLY_ERANIA
    }
  ];

  return (
    <section id="team" className="section-beige py-10 md:py-14" data-testid="team-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <div className="max-w-2xl mb-20">
          <p className="text-sm tracking-[0.3em] uppercase text-[#94A48A] font-medium mb-4">
            Your Guides
          </p>
          <h2 className="section-title mb-6">
            Meet Your <em>Coaches</em>
          </h2>
          <p className="section-subtitle">
            Former C-suite executives who've walked the path — from P&L leadership to strategic intelligence — bringing decades of real-world experience.
          </p>
        </div>

        {/* Coaches - Asymmetric Layout */}
        <div className="space-y-24">
          {coaches.map((coach, index) => (
            <div 
              key={index} 
              className={`grid grid-cols-1 lg:grid-cols-12 gap-8 items-start ${index === 1 ? 'pt-10' : ''}`}
              data-testid={`coach-card-${index}`}
            >
              {/* Image */}
              <div className={`lg:col-span-5 ${index % 2 === 1 ? 'lg:col-start-8' : ''} ${index === 0 ? 'mb-10 lg:mb-0' : ''}`}>
                <div className="coach-image-wrapper">
                  <img 
                    src={coach.image} 
                    alt={coach.name}
                    className="coach-image img-grayscale"
                  />
                </div>
              </div>
              
              {/* Content */}
              <div className={`lg:col-span-6 ${index % 2 === 1 ? 'lg:col-start-1 lg:row-start-1 mt-8 lg:mt-0' : ''}`}>
                <h3 className="text-4xl font-light text-black/90 mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  {coach.name}
                </h3>
                <p className="text-[#C27C62] font-medium text-sm tracking-wide mb-1">
                  {coach.title}
                </p>
                <p className="text-[#5A6F8C] text-sm tracking-wide mb-6">
                  {coach.subtitle}
                </p>
                
                <p className="text-black/60 leading-relaxed mb-4">
                  {coach.bio}
                </p>
                <p className="text-black/60 leading-relaxed mb-6">
                  {coach.bio2}
                </p>
                
                <p className="text-sm text-[#94A48A] font-medium tracking-wide mb-6">
                  {coach.credentials}
                </p>
                
                {coach.agency && (
                  <div className="bg-white/60 backdrop-blur-sm p-6 border border-black/5 mb-6">
                    <p className="text-sm text-black/70">
                      <strong>Founder & CEO of </strong>
                      <a 
                        href={coach.agency.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="link-terracotta font-semibold inline-flex items-center gap-1"
                        data-testid="brackett-agency-link"
                      >
                        {coach.agency.name}
                        <ExternalLink className="w-3 h-3" />
                      </a>
                      <span className="block mt-2 text-black/50">{coach.agency.description}</span>
                    </p>
                  </div>
                )}
                
                {coach.contactEmail && (
                  <a
                    href={`mailto:${coach.contactEmail}?subject=Executive Coaching Inquiry`}
                    className="btn-primary inline-flex items-center gap-2"
                    data-testid={`email-${coach.name.split(' ')[0].toLowerCase()}-button`}
                  >
                    <Mail className="w-4 h-4" />
                    Email {coach.name.split(' ')[0]}
                  </a>
                )}
                
                {coach.calendlyLink && (
                  <a
                    href={coach.calendlyLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-primary inline-flex items-center gap-2"
                    data-testid="schedule-erania-button"
                  >
                    <Calendar className="w-4 h-4" />
                    Book 15-Min Connect
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Insights Carousel Section
const InsightsSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const location = useLocation();
  
  const insights = [
    {
      category: "Legacy Building",
      title: "Architecting Your Legacy: Moving Beyond Career Milestones",
      description: "Why the most successful executives shift from collecting titles to crafting lasting impact.",
      content: `
        <h2>The Shift from Achievement to Impact</h2>
        <p>For most of our careers, we've been conditioned to measure success in titles, promotions, and compensation packages. We collect achievements like badges—each one validating our worth and propelling us toward the next milestone. But somewhere along the journey, usually in the midst of our most senior roles, a question begins to surface: <em>Is this all there is?</em></p>
        
        <p>This isn't a crisis of confidence. It's an evolution of consciousness. The most successful executives I've coached describe a moment when they realized that their next promotion wouldn't fill the void they were beginning to feel. They had climbed the mountain, only to discover that the view from the top wasn't as fulfilling as they'd imagined.</p>
        
        <h2>From Collecting to Crafting</h2>
        <p>Legacy building requires a fundamental shift in how we think about our careers. Instead of asking "What's my next role?" we begin asking "What do I want to be known for?" Instead of measuring success in quarterly results, we start thinking in terms of generational impact.</p>
        
        <p>This transition isn't about abandoning ambition—it's about redirecting it. The executives who make this shift successfully don't become less driven; they become more intentional. They stop chasing titles and start crafting contributions that will outlast their tenure.</p>
        
        <h2>The Three Pillars of Executive Legacy</h2>
        <p><strong>1. People Development:</strong> The leaders you've mentored, the careers you've accelerated, the potential you've unlocked in others. This is the legacy that multiplies long after you've moved on.</p>
        
        <p><strong>2. Institutional Wisdom:</strong> The systems, cultures, and ways of thinking you've embedded in organizations. These become part of the corporate DNA, influencing decisions you'll never see.</p>
        
        <p><strong>3. Industry Contribution:</strong> The ideas you've championed, the standards you've raised, the conversations you've advanced in your field. This is how you shape the future of your profession.</p>
        
        <h2>Making the Transition Intentionally</h2>
        <p>The executives who architect their legacy most effectively do so with intention. They don't wait for retirement to think about impact—they begin building it in their current roles. They ask themselves: "If I left tomorrow, what would remain?"</p>
        
        <p>This question isn't morbid; it's clarifying. It helps us distinguish between the urgent and the important, between the metrics that matter this quarter and the contributions that matter for decades.</p>
        
        <p>Your legacy isn't something that happens to you at the end of your career. It's something you architect every day, in every decision, in every interaction. The question is: are you building it intentionally?</p>
      `
    },
    {
      category: "Strategic Intelligence",
      title: "The Power of Strategic Intelligence in Decision-Making",
      description: "How data-driven insights reduce failure rates at the C-suite level.",
      content: `
        <h2>Beyond Gut Instinct: The New Executive Imperative</h2>
        <p>For generations, executive decision-making has relied heavily on experience and intuition. Seasoned leaders developed a "nose" for good decisions, built through years of pattern recognition and hard-won lessons. But in today's rapidly evolving business landscape, gut instinct alone is no longer sufficient.</p>
        
        <p>Strategic intelligence—the systematic use of data-driven insights to inform executive decisions—has emerged as a critical capability for modern leaders. This isn't about replacing human judgment; it's about augmenting it with predictive power that reduces risk and increases probability of success.</p>
        
        <h2>The Cost of Intuition-Only Decisions</h2>
        <p>Research consistently shows that major strategic initiatives fail at alarming rates. New product launches, market expansions, M&A integrations, digital transformations—across categories, failure rates hover between 60-80%. The common thread? Decisions made primarily on experience and intuition, without rigorous intelligence to validate assumptions.</p>
        
        <p>The executives who consistently beat these odds aren't necessarily smarter or more experienced. They're better informed. They've learned to complement their intuition with intelligence that reveals what their experience might miss.</p>
        
        <h2>Three Dimensions of Strategic Intelligence</h2>
        <p><strong>Customer Intelligence:</strong> Understanding not just what customers say they want, but what actually drives their choices. This means moving beyond surveys and focus groups to behavioral analytics and predictive modeling that reveal true preferences.</p>
        
        <p><strong>Market Intelligence:</strong> Seeing around corners before your competitors do. This requires systematic monitoring of market signals, competitive movements, and emerging trends—not as periodic exercises, but as continuous capabilities.</p>
        
        <p><strong>Organizational Intelligence:</strong> Understanding the true capabilities and limitations of your organization. Many strategies fail not because they were wrong for the market, but because they were wrong for the organization attempting to execute them.</p>
        
        <h2>Integrating Intelligence into Executive Practice</h2>
        <p>The most effective executives don't treat strategic intelligence as a separate function—they integrate it into their leadership practice. They've learned to ask better questions: "What does the data tell us?" before "What do we think?" They've built teams that challenge assumptions with evidence. They've created cultures where decisions are expected to be defended with intelligence, not just authority.</p>
        
        <p>This doesn't mean paralysis by analysis. The best leaders use intelligence to move faster, not slower. By reducing uncertainty upfront, they can commit more fully to their chosen path without the hedging that comes from unexamined assumptions.</p>
        
        <p>In an era of unprecedented complexity and velocity, strategic intelligence isn't a competitive advantage—it's a survival requirement. The executives who master it will lead organizations that consistently outperform. Those who don't will wonder why their experience keeps failing them.</p>
      `
    },
    {
      category: "Board Leadership",
      title: "Navigating Board Governance: Insights from the Boardroom",
      description: "Essential perspectives on board dynamics and governance excellence.",
      content: `
        <h2>The Invisible Architecture of Effective Boards</h2>
        <p>From the outside, corporate boards can seem like mysterious institutions—closed-door meetings where consequential decisions emerge through opaque processes. But having served on multiple boards and advised dozens of directors, I've come to understand that effective governance has a clear architecture. It's not mysterious; it's methodical.</p>
        
        <p>The boards that function well share common characteristics that go far beyond checking compliance boxes. They've built cultures of constructive challenge, mechanisms for accessing ground truth, and relationships that enable difficult conversations.</p>
        
        <h2>The Three Roles Every Director Must Master</h2>
        <p><strong>The Fiduciary Role:</strong> This is the foundation—ensuring the organization is well-managed, financially sound, and legally compliant. It's the role most directors understand intuitively, but it's only the beginning.</p>
        
        <p><strong>The Strategic Role:</strong> Beyond oversight, effective directors contribute to strategy. They bring outside perspectives, challenge assumptions, and help management see around corners. This requires engagement that goes deeper than quarterly presentations.</p>
        
        <p><strong>The Supportive Role:</strong> The best directors know when to step back and let management lead. They provide air cover during difficult periods, serve as sounding boards for CEOs facing lonely decisions, and help attract and retain top talent through their networks and endorsement.</p>
        
        <h2>The Dynamics That Make or Break Boards</h2>
        <p>Board effectiveness isn't determined by the credentials in the room—it's determined by the dynamics between those credentials. I've seen boards filled with impressive resumes that couldn't have a productive conversation, and boards with less pedigreed members that consistently added enormous value.</p>
        
        <p>The difference comes down to several factors: psychological safety that enables candid discussion, diversity of perspective that prevents groupthink, and meeting structures that allow for real deliberation rather than rubber-stamping.</p>
        
        <h2>Preparing for Board-Level Impact</h2>
        <p>For executives aspiring to board roles, the preparation begins long before the first invitation arrives. It starts with developing a genuine point of view on governance—not from reading about it, but from observing it, studying it, and forming opinions about what works.</p>
        
        <p>It continues with building relationships in the board community, understanding how directors are sourced and evaluated, and developing a reputation as someone who can contribute at this level. And it requires honest self-assessment: Are you ready to shift from operator to advisor? Can you influence without authority? Can you hold management accountable while remaining supportive?</p>
        
        <p>Board service is a privilege and a responsibility. For those who approach it with the right preparation and mindset, it's also an opportunity to extend your impact far beyond any single organization.</p>
      `
    },
    {
      category: "Executive Presence",
      title: "Building Executive Gravitas: Authentic Leadership",
      description: "The intersection of authenticity and authority in modern leadership.",
      content: `
        <h2>The Paradox of Executive Presence</h2>
        <p>Early in my career, I believed executive presence was something you performed—a persona you adopted in important meetings, a voice you put on for the boardroom. I studied leaders who seemed to have it and tried to mimic their behaviors. It never quite worked.</p>
        
        <p>What I eventually learned is that executive presence isn't a performance—it's a revelation. It's what emerges when you stop trying to be someone else and start being fully yourself, but with intention and awareness. The paradox is that the more authentic you become, the more authority you project.</p>
        
        <h2>The Three Dimensions of Gravitas</h2>
        <p><strong>Intellectual Gravitas:</strong> This is the weight of your thinking—the quality of your analysis, the depth of your insights, the clarity of your strategic vision. It's developed through rigorous learning, broad reading, and continuous engagement with complex problems. You can't fake intellectual gravitas; you have to earn it.</p>
        
        <p><strong>Emotional Gravitas:</strong> This is the steadiness of your presence—your ability to remain calm under pressure, to create safety for others, to hold space for difficult emotions without being destabilized by them. It comes from self-awareness and emotional regulation, developed through practices like reflection, feedback, and sometimes coaching.</p>
        
        <p><strong>Moral Gravitas:</strong> This is the foundation of trust—your demonstrated commitment to principles, your willingness to take stands, your consistency between words and actions over time. It can't be developed quickly; it's the accumulation of choices made when no one was watching.</p>
        
        <h2>Finding Your Authentic Voice</h2>
        <p>The leaders with the most compelling presence aren't those who've perfected a universal template. They're those who've discovered and refined their unique voice. Some lead with quiet intensity. Others with passionate advocacy. Some with warm approachability. Others with dignified reserve.</p>
        
        <p>Your authentic voice is the one that allows you to be fully present, that doesn't require energy to maintain, that actually improves under pressure rather than cracking. Finding it requires experimentation, feedback, and the courage to stop imitating others.</p>
        
        <h2>Presence in Practice</h2>
        <p>Executive presence isn't only visible in big moments—it's built in small ones. It's how you enter a room, how you listen in meetings, how you respond to challenges, how you treat people who can do nothing for you. It's the accumulation of countless micro-behaviors that either reinforce or undermine your authority.</p>
        
        <p>The executives who develop genuine gravitas pay attention to these details without becoming self-conscious about them. They've internalized the behaviors so thoroughly that they emerge naturally, allowing their attention to focus outward rather than inward.</p>
        
        <p>Building executive presence is a lifetime practice. It's never complete, always evolving, continuously refined through experience and reflection. But for those committed to the journey, it becomes the foundation for impact that extends far beyond any single role or organization.</p>
      `
    },
    {
      category: "Personal Brand",
      title: "From Strategy to Sustainable Impact",
      description: "Creating a personalized playbook for measurable outcomes.",
      content: `
        <h2>The Gap Between Intention and Impact</h2>
        <p>Every executive I coach arrives with good intentions. They want to lead more effectively, contribute more meaningfully, leave a lasting mark on their organizations. Yet many struggle to translate these intentions into sustained impact. The gap isn't motivation—it's methodology.</p>
        
        <p>Impact doesn't happen through inspiration alone. It requires a systematic approach: clear goals, deliberate practices, feedback mechanisms, and continuous refinement. It requires, in short, a personal playbook.</p>
        
        <h2>Designing Your Personal Playbook</h2>
        <p>A personal playbook isn't a to-do list or a development plan. It's a comprehensive framework for how you'll show up as a leader—the principles that guide your decisions, the practices that structure your days, the metrics that measure your progress.</p>
        
        <p><strong>Start with Values:</strong> What do you stand for? What lines won't you cross? What principles will guide your choices when the path isn't clear? Your values aren't aspirations—they're commitments you've already demonstrated through action.</p>
        
        <p><strong>Define Your Contribution:</strong> Given your unique combination of experiences, capabilities, and passions, what can you contribute that few others can? This isn't about being the best at everything—it's about being essential in specific ways.</p>
        
        <p><strong>Identify Your Leverage Points:</strong> Where can you have the greatest impact with the resources (time, energy, attention, relationships) available to you? Effective executives don't try to change everything—they identify the few interventions that will create disproportionate results.</p>
        
        <h2>From Planning to Practice</h2>
        <p>The best playbook is worthless if it sits in a drawer. Sustainable impact requires translating strategy into daily practice. This means:</p>
        
        <p><strong>Rituals and Routines:</strong> The practices you'll maintain regardless of circumstances—your morning routine, your weekly review, your monthly reflection. These create the structure within which impact becomes possible.</p>
        
        <p><strong>Feedback Loops:</strong> How will you know if you're making progress? What signals will you monitor? Who will tell you the truth when you're off track? Without feedback, good intentions drift into comfortable patterns.</p>
        
        <p><strong>Renewal Practices:</strong> Sustainable impact requires sustainable energy. What will you do to maintain your physical, emotional, and intellectual vitality? The executives who burn out don't leave legacies—they leave cautionary tales.</p>
        
        <h2>The Long Game</h2>
        <p>The most important word in "sustainable impact" is sustainable. Any executive can sprint—can deliver an impressive quarter, execute a successful initiative, make a mark in a new role. The question is: can you maintain that level of contribution over years and decades?</p>
        
        <p>The executives who achieve lasting impact think in longer time horizons than their peers. They make choices that might sacrifice short-term recognition for long-term significance. They build slowly and deliberately, knowing that the most meaningful contributions compound over time.</p>
        
        <p>Your playbook isn't a document you complete—it's a practice you maintain. It evolves as you evolve, becoming more refined and more powerful with each iteration. Start where you are. Use what you have. Do what you can. And keep going.</p>
      `
    }
  ];

  // Check for article URL parameter on mount
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const articleTitle = params.get('article');
    if (articleTitle) {
      const article = insights.find(i => i.title === decodeURIComponent(articleTitle));
      if (article) {
        setSelectedArticle(article);
        // Scroll to insights section
        setTimeout(() => {
          document.querySelector('#insights')?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    }
  }, [location.search]);

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % insights.length);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + insights.length) % insights.length);
  };

  return (
    <section id="insights" className="section-cream py-10 md:py-14" data-testid="insights-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between mb-8 gap-6">
          <div className="max-w-xl">
            <p className="text-sm tracking-[0.3em] uppercase text-[#C27C62] font-medium mb-4">
              Insights
            </p>
            <h2 className="section-title">
              Leadership <em>Perspectives</em>
            </h2>
          </div>
          
          <div className="flex gap-3">
            <button
              onClick={prevSlide}
              className="carousel-btn"
              aria-label="Previous insight"
              data-testid="insights-prev-btn"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={nextSlide}
              className="carousel-btn"
              aria-label="Next insight"
              data-testid="insights-next-btn"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Carousel */}
        <div className="overflow-hidden">
          <div 
            className="flex transition-transform duration-500 ease-out gap-6"
            style={{ transform: `translateX(-${currentIndex * (100 / 3)}%)` }}
          >
            {insights.map((insight, index) => (
              <div
                key={index}
                className="insight-card flex-shrink-0 w-full md:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)] cursor-pointer"
                data-testid={`insight-card-${index}`}
                onClick={() => setSelectedArticle(insight)}
              >
                <span className="insight-category">{insight.category}</span>
                <h3 className="text-xl font-light text-black/90 mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  {insight.title}
                </h3>
                <p className="text-black/50 text-sm leading-relaxed mb-6">
                  {insight.description}
                </p>
                <span className="link-terracotta text-sm inline-flex items-center gap-2" data-testid={`insight-link-${index}`}>
                  Read Article <ArrowRight className="w-4 h-4" />
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Dots */}
        <div className="flex justify-center gap-2 mt-10">
          {insights.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={`w-2 h-2 rounded-full transition-all ${
                index === currentIndex ? 'bg-[#C27C62] w-8' : 'bg-black/20'
              }`}
              aria-label={`Go to slide ${index + 1}`}
              data-testid={`insight-dot-${index}`}
            />
          ))}
        </div>
      </div>

      {/* Article Modal */}
      {selectedArticle && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedArticle(null)}
        >
          <div 
            className="bg-[#F5F1E6] max-w-3xl w-full max-h-[90vh] overflow-y-auto rounded-sm shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="sticky top-0 bg-[#F5F1E6] border-b border-black/10 p-6 flex justify-between items-start">
              <div>
                <span className="text-xs tracking-[0.2em] uppercase text-[#C27C62] font-medium">
                  {selectedArticle.category}
                </span>
                <h2 className="text-2xl md:text-3xl font-light text-black/90 mt-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  {selectedArticle.title}
                </h2>
              </div>
              <button
                onClick={() => setSelectedArticle(null)}
                className="p-2 hover:bg-black/5 rounded-full transition-colors"
                aria-label="Close article"
                data-testid="close-article-modal"
              >
                <X className="w-6 h-6 text-black/60" />
              </button>
            </div>
            
            {/* Modal Content */}
            <div 
              className="p-6 md:p-10 prose prose-lg max-w-none"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              <div 
                className="article-content"
                dangerouslySetInnerHTML={{ __html: selectedArticle.content }}
              />
            </div>
            
            {/* Modal Footer */}
            <div className="border-t border-black/10 p-6 flex justify-between items-center">
              <div className="flex items-center gap-3">
                <span className="text-sm text-black/50" style={{ fontFamily: 'Manrope, sans-serif' }}>Share:</span>
                <button
                  onClick={() => {
                    const url = `${window.location.origin}?article=${encodeURIComponent(selectedArticle.title)}`;
                    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`, '_blank', 'width=600,height=400');
                  }}
                  className="p-2 hover:bg-black/5 rounded-full transition-colors"
                  aria-label="Share on LinkedIn"
                  data-testid="share-linkedin"
                >
                  <Linkedin className="w-5 h-5 text-black/60" />
                </button>
                <button
                  onClick={() => {
                    const url = `${window.location.origin}?article=${encodeURIComponent(selectedArticle.title)}`;
                    window.open(`https://substack.com/share?url=${encodeURIComponent(url)}&title=${encodeURIComponent(selectedArticle.title)}`, '_blank', 'width=600,height=400');
                  }}
                  className="p-2 hover:bg-black/5 rounded-full transition-colors"
                  aria-label="Share on Substack"
                  data-testid="share-substack"
                >
                  <FileText className="w-5 h-5 text-black/60" />
                </button>
                <button
                  onClick={() => {
                    const url = `${window.location.origin}?article=${encodeURIComponent(selectedArticle.title)}`;
                    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank', 'width=600,height=400');
                  }}
                  className="p-2 hover:bg-black/5 rounded-full transition-colors"
                  aria-label="Share on Facebook"
                  data-testid="share-facebook"
                >
                  <Facebook className="w-5 h-5 text-black/60" />
                </button>
                <button
                  onClick={() => {
                    const url = `${window.location.origin}?article=${encodeURIComponent(selectedArticle.title)}`;
                    navigator.clipboard.writeText(url);
                    alert('Link copied to clipboard!');
                  }}
                  className="p-2 hover:bg-black/5 rounded-full transition-colors"
                  aria-label="Copy link"
                  data-testid="share-copy-link"
                >
                  <Link2 className="w-5 h-5 text-black/60" />
                </button>
              </div>
              <Button
                onClick={() => setSelectedArticle(null)}
                className="btn-primary text-sm"
                data-testid="close-article-btn"
              >
                Close Article
              </Button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

// Podcast Section
const PodcastSection = () => {
  const [activeFilter, setActiveFilter] = useState('all');
  const [showAll, setShowAll] = useState(false);
  const INITIAL_SHOW_COUNT = 6;

  const episodes = [
    // Season 1 Episodes
    {
      date: "Season 1",
      season: "season1",
      featured: true,
      title: "Why We Made Redefining Return",
      description: "Chereé and Erania introduce themselves, share their transition stories, and explain why this podcast needed to exist.",
      duration: "45:00",
      comingSoon: false,
      episodeNum: 1,
      image: "https://static.prod-images.emergentagent.com/jobs/5d03a66d-48ea-432c-873f-a6fcec69fb36/images/d3e6ebf17864c7383f219654a9ee66d9a72d8c1efa517813cd33b22b6bfef687.jpeg",
      youtubeUrl: "https://youtu.be/r22G7n-bgqo"
    },
    {
      date: "Season 1",
      season: "season1",
      featured: true,
      title: "The Identity Crisis No One Warns You About",
      description: "What happens when 90% of your identity is your job title? Exploring the identity crisis that hits after leaving corporate.",
      duration: "42:00",
      comingSoon: false,
      episodeNum: 2,
      image: "https://static.prod-images.emergentagent.com/jobs/5d03a66d-48ea-432c-873f-a6fcec69fb36/images/40177b357651bf8bf82d47f52c7ef6a8bd88638b81b5b984d050b2a7bc56efec.jpeg",
      youtubeUrl: "https://youtu.be/vuj3c2xVqHw"
    },
    {
      date: "Season 1",
      season: "season1",
      featured: false,
      title: "How Much Money Do You Actually Need to Leave?",
      description: "The financial realities of leaving corporate. Calculating exit funds and making hard decisions under uncertainty.",
      duration: "38:00",
      comingSoon: false,
      episodeNum: 3,
      image: "https://static.prod-images.emergentagent.com/jobs/5d03a66d-48ea-432c-873f-a6fcec69fb36/images/9b7b62b4dda6e198ba44146f03c1b668089953957d320afa85660b0bd1339d20.jpeg",
      youtubeUrl: "https://youtu.be/59lqheG8-PA"
    },
    {
      date: "Season 1",
      season: "season1",
      featured: false,
      title: "When Your Career Transition Strains Your Marriage",
      description: "Career transitions strain partnerships. Discussing communication during upheaval and navigating relationship stress.",
      duration: "41:00",
      comingSoon: true,
      episodeNum: 4
    },
    {
      date: "Season 1",
      season: "season1",
      featured: false,
      title: "Burnout Recovery: What Actually Works",
      description: "Burnout is physical and existential exhaustion. Why high achievers are vulnerable and what actually helps.",
      duration: "44:00",
      comingSoon: true,
      episodeNum: 5
    },
    {
      date: "Season 1",
      season: "season1",
      featured: false,
      title: "The Career Pivot vs. The Career Pause",
      description: "Sometimes you need a pivot, sometimes a pause. How to tell the difference and structure a strategic pause.",
      duration: "39:00",
      comingSoon: true,
      episodeNum: 6
    },
    {
      date: "Season 1",
      season: "season1",
      featured: false,
      title: "Building Your Board of Advisors",
      description: "Transitions are lonely. How to build a personal board of advisors including friends, mentors, and peers.",
      duration: "40:00",
      comingSoon: true,
      episodeNum: 7
    },
    {
      date: "Season 1",
      season: "season1",
      featured: false,
      title: "The Loneliness of Executive Transitions",
      description: "Executive transitions are isolating. Finding connection when you feel you should have it together but don't.",
      duration: "43:00",
      comingSoon: true,
      episodeNum: 8
    },
    {
      date: "Season 1",
      season: "season1",
      featured: false,
      title: "Therapy Isn't Weakness—It's Strategy",
      description: "Destigmatizing mental health support for executives. Therapy as a strategic investment.",
      duration: "37:00",
      comingSoon: true,
      episodeNum: 9
    },
    {
      date: "Season 1",
      season: "season1",
      featured: false,
      title: "When Going Back to Corporate Is the Smart Move",
      description: "Returning to corporate isn't failure. How to negotiate a return with leverage and better terms.",
      duration: "42:00",
      comingSoon: true,
      episodeNum: 10
    },
    {
      date: "Season 1",
      season: "season1",
      featured: false,
      title: "Redefining Success: What It Actually Means Now",
      description: "Moving from external validation (titles, money) to internal alignment (values, freedom).",
      duration: "45:00",
      comingSoon: true,
      episodeNum: 11
    },
    {
      date: "Season 1",
      season: "season1",
      featured: false,
      title: "Small Wins Matter When Everything Feels Uncertain",
      description: "The power of incremental progress when the path isn't clear. Celebrating milestones in the messy middle.",
      duration: "38:00",
      comingSoon: true,
      episodeNum: 12
    },
    // Season 2 Preview Episodes
    {
      date: "Season 2",
      season: "season2",
      featured: false,
      title: "The Grief of Leaving: Why Transitions Feel Like Loss",
      description: "Processing the emotional loss of a career identity.",
      duration: "40:00",
      comingSoon: true,
      episodeNum: 13
    },
    {
      date: "Season 2",
      season: "season2",
      featured: false,
      title: "Building a Portfolio Career That Actually Pays the Bills",
      description: "Tactical guide to multiple income streams.",
      duration: "42:00",
      comingSoon: true,
      episodeNum: 14
    },
    {
      date: "Season 2",
      season: "season2",
      featured: false,
      title: "Financial Planning for the Long Game",
      description: "Looking beyond the immediate exit fund.",
      duration: "38:00",
      comingSoon: true,
      episodeNum: 15
    },
    {
      date: "Season 2",
      season: "season2",
      featured: false,
      title: "How to Ask for Help When You're Used to Being in Charge",
      description: "Overcoming the 'hero leader' complex.",
      duration: "41:00",
      comingSoon: true,
      episodeNum: 16
    },
    {
      date: "Season 2",
      season: "season2",
      featured: false,
      title: "Dating and Relationships Post-Corporate: Starting Over",
      description: "Navigating new relationships without the title crutch.",
      duration: "39:00",
      comingSoon: true,
      episodeNum: 17
    },
    {
      date: "Season 2",
      season: "season2",
      featured: false,
      title: "The Power of Rituals and Routines During Chaos",
      description: "Anchoring yourself when days lack structure.",
      duration: "36:00",
      comingSoon: true,
      episodeNum: 18
    },
    {
      date: "Season 2",
      season: "season2",
      featured: false,
      title: "Board Seats as a Transition Path",
      description: "Practical steps to securing board roles.",
      duration: "44:00",
      comingSoon: true,
      episodeNum: 19
    },
    {
      date: "Season 2",
      season: "season2",
      featured: false,
      title: "Imposter Syndrome at the Executive Level",
      description: "Addressing self-doubt even at the C-suite level.",
      duration: "40:00",
      comingSoon: true,
      episodeNum: 20
    },
    {
      date: "Season 2",
      season: "season2",
      featured: false,
      title: "The Art of the Strategic Sabbatical",
      description: "How to take time off without derailing your career value.",
      duration: "37:00",
      comingSoon: true,
      episodeNum: 21
    },
    {
      date: "Season 2",
      season: "season2",
      featured: false,
      title: "Forward Motion Doesn't Require a Clear Path",
      description: "Taking action before you have the 5-year plan.",
      duration: "43:00",
      comingSoon: true,
      episodeNum: 22
    }
  ];

  const filteredEpisodes = episodes.filter(ep => {
    if (activeFilter === 'all') return true;
    if (activeFilter === 'featured') return ep.featured;
    return ep.season === activeFilter;
  });

  // Show only first 6 episodes unless expanded
  const displayedEpisodes = showAll ? filteredEpisodes : filteredEpisodes.slice(0, INITIAL_SHOW_COUNT);
  const hasMoreEpisodes = filteredEpisodes.length > INITIAL_SHOW_COUNT;
  const remainingCount = filteredEpisodes.length - INITIAL_SHOW_COUNT;

  // Reset showAll when filter changes
  const handleFilterChange = (filter) => {
    setActiveFilter(filter);
    setShowAll(false);
  };

  return (
    <section id="podcast" className="section-beige py-10 md:py-14" data-testid="podcast-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-8">
          <div className="lg:col-span-6">
            <p className="text-sm tracking-[0.3em] uppercase text-[#5A6F8C] font-medium mb-4">
              The Podcast
            </p>
            <h2 className="section-title mb-6">
              <em>Redefining</em> Return
            </h2>
            <p className="section-subtitle mb-6">
              Authentic conversations about navigating executive transitions and architecting your legacy.
            </p>
            
            {/* Podcast Subscribe Links */}
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-sm text-[#666] font-medium">Subscribe:</span>
              <a 
                href="https://www.youtube.com/@RedefiningReturnPodcast" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#FF0000] text-white text-sm font-medium rounded-full hover:bg-[#CC0000] transition-colors"
                data-testid="podcast-subscribe-youtube"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
                YouTube
              </a>
              <a 
                href={`${process.env.REACT_APP_BACKEND_URL}/api/podcast/feed.xml`}
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#F5712C] text-white text-sm font-medium rounded-full hover:bg-[#E5611C] transition-colors"
                data-testid="podcast-subscribe-rss"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M6.18 15.64a2.18 2.18 0 0 1 2.18 2.18C8.36 19 7.38 20 6.18 20C5 20 4 19 4 17.82a2.18 2.18 0 0 1 2.18-2.18M4 4.44A15.56 15.56 0 0 1 19.56 20h-2.83A12.73 12.73 0 0 0 4 7.27V4.44m0 5.66a9.9 9.9 0 0 1 9.9 9.9h-2.83A7.07 7.07 0 0 0 4 12.93V10.1z"/></svg>
                RSS Feed
              </a>
            </div>
          </div>
          
          <div className="lg:col-span-6 flex items-end">
            <div className="flex flex-wrap gap-2">
              {[
                { key: 'all', label: 'All Episodes' },
                { key: 'season2', label: 'Season 2' },
                { key: 'season1', label: 'Season 1' },
                { key: 'featured', label: 'Featured' }
              ].map((filter) => (
                <button
                  key={filter.key}
                  onClick={() => handleFilterChange(filter.key)}
                  className={`filter-btn ${activeFilter === filter.key ? 'active' : ''}`}
                  data-testid={`podcast-filter-${filter.key}`}
                >
                  {filter.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Episodes Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {displayedEpisodes.map((episode, index) => (
            <div
              key={index}
              className={`podcast-episode hover-lift ${episode.comingSoon ? 'opacity-80' : ''}`}
              data-testid={`podcast-episode-${index}`}
            >
              {episode.image && (
                <div className="mb-4 -mx-6 -mt-6">
                  <img 
                    src={episode.image} 
                    alt={episode.title}
                    className="w-full h-40 object-cover"
                  />
                </div>
              )}
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <span className="episode-date">
                  Ep {episode.episodeNum} • {episode.season === 'season2' ? 'Season 2' : 'Season 1'}
                </span>
                {episode.comingSoon && (
                  <span className="text-xs font-medium px-2 py-1 bg-[#C27C62]/20 text-[#C27C62] rounded-full">
                    Coming Soon
                  </span>
                )}
              </div>
              <h3 className="text-lg font-light text-black/90 mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                {episode.title}
              </h3>
              <p className="text-black/50 text-sm leading-relaxed mb-6">
                {episode.description}
              </p>
              <div className="flex items-center justify-between">
                {episode.comingSoon ? (
                  <span className="text-[#5A6F8C] text-sm font-medium">
                    Available Soon
                  </span>
                ) : (
                  <a
                    href={episode.youtubeUrl || YOUTUBE_CHANNEL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-[#94A48A] hover:text-[#C27C62] transition-colors text-sm font-medium"
                    data-testid={`podcast-listen-${index}`}
                  >
                    <Play className="w-4 h-4" /> Listen Now
                  </a>
                )}
                <span className="text-black/30 text-xs flex items-center gap-1">
                  <Clock className="w-3 h-3" /> {episode.duration}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Show More / Show Less Button */}
        {hasMoreEpisodes && (
          <div className="mt-10 text-center">
            <button
              onClick={() => setShowAll(!showAll)}
              className="inline-flex items-center gap-2 px-8 py-4 border-2 border-[#C27C62] text-[#C27C62] rounded-full font-medium hover:bg-[#C27C62] hover:text-white transition-all duration-300"
              data-testid="podcast-show-more-btn"
            >
              {showAll ? (
                <>
                  <ChevronLeft className="w-4 h-4 rotate-90" />
                  Show Less
                </>
              ) : (
                <>
                  Show {remainingCount} More Episodes
                  <ChevronRight className="w-4 h-4 -rotate-90" />
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

// Press Kit Page
const PressPage = () => {
  const [scrolled, setScrolled] = useState(false);
  const [showNewsletterModal, setShowNewsletterModal] = useState(false);
  
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    window.scrollTo(0, 0); // Scroll to top on page load
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Show newsletter popup after 15 seconds (only once per session)
  useEffect(() => {
    const hasSeenPopup = sessionStorage.getItem('newsletter_popup_shown');
    if (!hasSeenPopup) {
      const timer = setTimeout(() => {
        setShowNewsletterModal(true);
        sessionStorage.setItem('newsletter_popup_shown', 'true');
      }, 15000);
      return () => clearTimeout(timer);
    }
  }, []);

  const podcastTopics = [
    "Navigating Career Transitions After Executive Breaks",
    "The Messy Middle: Embracing Uncertainty in Leadership",
    "Redefining Success on Your Own Terms",
    "Building Executive Presence Through Authenticity",
    "From Corporate to Board: Preparing for Governance Roles",
    "The Power of Strategic Intelligence in Decision-Making",
    "Women in the C-Suite: Breaking Barriers & Building Legacy",
    "AI Governance: What Every Executive Needs to Know",
    "The First 90 Days: Returning to Executive Leadership",
    "Architecting Your Leadership Legacy"
  ];

  const speakerTopics = [
    {
      title: "Redefining Return: The Executive Comeback",
      description: "How senior leaders can strategically navigate career transitions and return stronger than ever."
    },
    {
      title: "The Enso of Leadership: Transformation Through Intention",
      description: "Embracing the circular nature of growth and finding completeness in continuous evolution."
    },
    {
      title: "Architecting Your Legacy: Beyond Career Milestones",
      description: "Shifting from achievement collection to intentional impact creation."
    },
    {
      title: "Executive Gravitas: The Intersection of Authenticity & Authority",
      description: "Building genuine presence that commands respect while staying true to yourself."
    },
    {
      title: "The Velocity Protocol: AI Governance Without Chaos",
      description: "Implementing board-ready AI frameworks in weeks, not months."
    },
    {
      title: "Strategic Intelligence for C-Suite Decision Making",
      description: "Leveraging data-driven insights to reduce risk and accelerate growth."
    },
    {
      title: "Board Readiness: Positioning for Governance Opportunities",
      description: "What aspiring board members need to know about modern governance."
    },
    {
      title: "Women's Leadership: Health, Performance & Executive Success",
      description: "The often-unspoken connection between wellness and sustained leadership excellence."
    }
  ];

  const handleDownloadPressKit = () => {
    // Create press kit content
    const pressKitContent = `
REDEFINING RETURN - PRESS KIT
Executive Coaching for Leaders Architecting Their Legacy
================================================================================

ABOUT REDEFINING RETURN
--------------------------------------------------------------------------------
Redefining Return is an executive coaching practice dedicated to supporting 
senior leaders through their most meaningful career transitions. Founded by 
former C-suite executives who've walked the path, we combine decades of 
leadership experience with proven coaching frameworks to help executives 
architect their lasting legacy.

Our name reflects the Enso circle — a Zen symbol representing transformation, 
enlightenment, and the moment when the mind is free to let the body create. 
We believe every transition is an opportunity for profound growth.

KEY STATISTICS
--------------------------------------------------------------------------------
• 150+ Senior Leaders Coached
• 40+ Years Combined Executive Experience  
• $500M+ P&L Leadership Experience
• Industries: Healthcare, Technology, Manufacturing, Consumer Goods, 
  Financial Services, Life Sciences, Industrial

================================================================================

COACH BIOS
================================================================================

CHEREÉ JOHNSON
Former CLO & General Counsel | Board Advisor | Executive Coach
--------------------------------------------------------------------------------

SHORT BIO (50 words):
Chereé Johnson is an award-winning C-suite executive and executive coach 
specializing in leadership presence, strategic resilience, and board-level 
navigation. As Founder & CEO of Haswell & Co., she created The Velocity 
Protocol™ for AI governance. She holds a JD and BS in Chemical Engineering.

LONG BIO (150 words):
Chereé Johnson is an award-winning C-suite executive and trusted strategic 
advisor to Boards of Directors across publicly traded and privately held 
companies spanning consumer, manufacturing, life sciences, medical technology, 
and industrial sectors. With extensive operational experience leading global, 
cross-functional teams through complex transitions including significant M&A, 
insolvency proceedings, shareholder activism, and government enforcement matters, 
Chereé brings unparalleled expertise in organizational development, executive 
leadership, and navigating bet-the-company business challenges.

She specializes in helping senior leaders develop authentic leadership presence, 
strategic resilience, and the executive gravitas needed to architect their 
legacy and navigate board-level challenges.

As Founder & CEO of Haswell & Co., Chereé created The Velocity Protocol™ — 
delivering board-ready AI governance frameworks in 6-8 weeks.

Credentials: JD, BS Chemical Engineering
Contact: info@redefiningreturn.com

--------------------------------------------------------------------------------

ERANIA BRACKETT
Former CMO & SVP | Former Board Director | Executive Coach
--------------------------------------------------------------------------------

SHORT BIO (50 words):
Erania Brackett is a seasoned C-suite executive and executive coach specializing 
in personal branding, leadership legacy, and executive presence. As Founder & CEO 
of Brackett Agency, she delivers Stanford-born predictive intelligence for 
strategic decision-making. She holds a BS in Chemical Engineering and Executive MBA.

LONG BIO (150 words):
Erania Brackett is a seasoned executive with extensive C-suite leadership across 
publicly traded companies in healthcare, technology, and consumer sectors. She has 
led global business units, managed significant P&L responsibilities, and driven 
transformative growth as CMO, Chief Customer Experience Officer, and Head of ESG. 
Her strategic expertise spans digital marketing transformation, brand strategy 
development, product commercialization, and major business integrations.

Currently serving as a Board Director for multiple organizations and having 
previously served on a public company board, Erania brings deep governance 
experience alongside her operational leadership. She specializes in helping 
executives craft compelling personal brands, architect their leadership legacy, 
and build the executive presence needed to secure senior director, VP, and 
C-level opportunities.

As Founder & CEO of Brackett Agency, she delivers fractional CSO & CMO services 
powered by Stanford-born predictive intelligence.

Credentials: BS Chemical Engineering, Executive MBA
Scheduling: https://calendly.com/erania/redefining-return-executive-coaching-interest-session

================================================================================

BRAND GUIDELINES
================================================================================

COLOR PALETTE
--------------------------------------------------------------------------------
• Deep Black: #000000 - Primary text, footer backgrounds
• Soft Cream: #F5F1E6 - Primary backgrounds
• Beige Tone: #E0D4B9 - Secondary backgrounds, accents
• Warm Terracotta: #C27C62 - Primary accent, CTAs, emphasis
• Sage Green: #94A48A - Secondary accent, success states
• Soft Indigo Blue: #5A6F8C - Tertiary accent, links
• Warm Gold: #D4B06C - Highlights, statistics
• Aged Gold: #B89B60 - Secondary gold accent

TYPOGRAPHY
--------------------------------------------------------------------------------
• Headlines: Cormorant Garamond (Light, Regular, Medium)
• Body Text: Manrope (Light, Regular, Medium)

LOGO USAGE
--------------------------------------------------------------------------------
The Redefining Return logo features an Enso circle — representing transformation, 
wholeness, and enlightenment. The brushstroke style reflects the organic, 
human-centered nature of our coaching approach.

• Minimum clear space: Equal to the height of the "R" in "Return"
• Do not distort, rotate, or alter logo colors
• Use on light backgrounds; invert for dark backgrounds

================================================================================

PODCAST TOPICS
================================================================================
${podcastTopics.map((topic, i) => `${i + 1}. ${topic}`).join('\n')}

================================================================================

SPEAKER TOPICS
================================================================================
${speakerTopics.map((topic, i) => `
${i + 1}. ${topic.title}
   ${topic.description}
`).join('')}

================================================================================

MEDIA CONTACT
================================================================================
For press inquiries, interview requests, or speaker bookings:

Email: info@redefiningreturn.com
Website: redefiningreturn.com

================================================================================

© 2025 Redefining Return. All rights reserved.
    `;

    // Create and download the file
    const blob = new Blob([pressKitContent], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'Redefining_Return_Press_Kit.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    
    toast.success("Press Kit Downloaded!", {
      description: "Thank you for your interest in Redefining Return."
    });
  };

  const pressContent = (
    <section id="press" className="section-cream py-10 md:py-14" data-testid="press-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <div className="max-w-2xl mb-10">
          <p className="text-sm tracking-[0.3em] uppercase text-[#5A6F8C] font-medium mb-4">
            For Media
          </p>
          <h2 className="section-title mb-6">
            Press <em>Kit</em>
          </h2>
          <p className="section-subtitle">
            Resources for media, event organizers, and podcast hosts. Download our complete press kit or explore individual assets below.
          </p>
        </div>

        {/* Download Button */}
        <div className="mb-10">
          <button
            onClick={handleDownloadPressKit}
            className="btn-primary inline-flex items-center gap-3 text-lg px-10 py-5"
            data-testid="download-press-kit-btn"
          >
            <Download className="w-5 h-5" />
            Download Complete Press Kit
          </button>
        </div>

        {/* Quick Facts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          <div className="editorial-card">
            <div className="flex items-center gap-3 mb-6">
              <FileText className="w-6 h-6 text-[#C27C62]" />
              <h3 className="text-2xl font-light text-black/90" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Quick Facts
              </h3>
            </div>
            <ul className="space-y-4 text-black/70">
              <li className="flex justify-between border-b border-black/10 pb-3">
                <span>Founded</span>
                <span className="font-medium text-black/90">2025</span>
              </li>
              <li className="flex justify-between border-b border-black/10 pb-3">
                <span>Senior Leaders Coached</span>
                <span className="font-medium text-[#D4B06C]">150+</span>
              </li>
              <li className="flex justify-between border-b border-black/10 pb-3">
                <span>Combined Executive Experience</span>
                <span className="font-medium text-[#D4B06C]">40+ Years</span>
              </li>
              <li className="flex justify-between border-b border-black/10 pb-3">
                <span>P&L Leadership</span>
                <span className="font-medium text-[#D4B06C]">$500M+</span>
              </li>
              <li className="flex justify-between">
                <span>Focus</span>
                <span className="font-medium text-black/90">Executive Coaching & Transitions</span>
              </li>
            </ul>
          </div>

          <div className="editorial-card">
            <div className="flex items-center gap-3 mb-6">
              <Mail className="w-6 h-6 text-[#C27C62]" />
              <h3 className="text-2xl font-light text-black/90" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Media Contact
              </h3>
            </div>
            <p className="text-black/60 mb-6">
              For press inquiries, interview requests, or speaker bookings, please contact:
            </p>
            <div className="space-y-3">
              <a 
                href="mailto:info@redefiningreturn.com?subject=Media Inquiry - Redefining Return" 
                className="link-terracotta text-lg font-medium flex items-center gap-2"
              >
                <Mail className="w-4 h-4" />
                info@redefiningreturn.com
              </a>
            </div>
          </div>
        </div>

        {/* Brand Colors */}
        <div className="mb-20">
          <h3 className="text-2xl font-light text-black/90 mb-8" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Brand <em>Colors</em>
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
            {[
              { name: 'Deep Black', hex: '#000000' },
              { name: 'Soft Cream', hex: '#F5F1E6' },
              { name: 'Beige Tone', hex: '#E0D4B9' },
              { name: 'Terracotta', hex: '#C27C62' },
              { name: 'Sage Green', hex: '#94A48A' },
              { name: 'Soft Indigo', hex: '#5A6F8C' },
              { name: 'Warm Gold', hex: '#D4B06C' },
              { name: 'Aged Gold', hex: '#B89B60' }
            ].map((color, i) => (
              <div key={i} className="text-center">
                <div 
                  className="w-full aspect-square rounded-sm mb-2 border border-black/10"
                  style={{ backgroundColor: color.hex }}
                />
                <p className="text-xs font-medium text-black/70">{color.name}</p>
                <p className="text-xs text-black/40">{color.hex}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Podcast Topics */}
        <div className="mb-20">
          <div className="flex items-center gap-3 mb-8">
            <Mic className="w-6 h-6 text-[#94A48A]" />
            <h3 className="text-2xl font-light text-black/90" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Podcast <em>Topics</em>
            </h3>
          </div>
          <p className="text-black/60 mb-8 max-w-2xl">
            Our coaches are available for podcast interviews on the following topics:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {podcastTopics.map((topic, i) => (
              <div key={i} className="flex items-start gap-3 p-4 bg-white/50 border border-black/5">
                <span className="text-[#C27C62] font-medium">{String(i + 1).padStart(2, '0')}</span>
                <span className="text-black/70">{topic}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Speaker Topics */}
        <div>
          <div className="flex items-center gap-3 mb-8">
            <Presentation className="w-6 h-6 text-[#5A6F8C]" />
            <h3 className="text-2xl font-light text-black/90" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Speaking <em>Topics</em>
            </h3>
          </div>
          <p className="text-black/60 mb-8 max-w-2xl">
            Book our coaches for keynotes, workshops, and panel discussions:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {speakerTopics.map((topic, i) => (
              <div key={i} className="editorial-card hover-lift">
                <h4 className="text-lg font-medium text-black/90 mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  {topic.title}
                </h4>
                <p className="text-black/50 text-sm">{topic.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );

  return (
    <div className="min-h-screen bg-[#F5F1E6]">
      <div className="grain-overlay" />
      <Navigation scrolled={scrolled} activeSection="" />
      <div className="pt-20">
        {pressContent}
      </div>
      <FooterSection onOpenNewsletterModal={() => setShowNewsletterModal(true)} />
      <NewsletterModal 
        isOpen={showNewsletterModal} 
        onClose={() => setShowNewsletterModal(false)} 
      />
      <Toaster position="top-right" />
    </div>
  );
};

// Newsletter Section
const NewsletterSection = () => {
  const handleSubscribe = (e) => {
    e.preventDefault();
    toast.success("Welcome to our community!", {
      description: "You'll receive insights and episodes in your inbox."
    });
  };

  return (
    <section className="newsletter-section" data-testid="newsletter-section">
      <div className="max-w-3xl mx-auto px-6 lg:px-12 text-center">
        <h3 className="text-3xl font-light text-black/90 mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
          Stay <em className="text-[#C27C62]">Connected</em>
        </h3>
        <p className="text-black/50 mb-8 max-w-md mx-auto">
          Get coaching insights, podcast episodes, and exclusive resources delivered to your inbox.
        </p>
        <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-4 justify-center max-w-md mx-auto">
          <input
            type="email"
            placeholder="Your email address"
            required
            className="flex-1 px-6 py-4 bg-white/80 border border-black/10 rounded-full focus:outline-none focus:border-[#C27C62] transition-colors"
            data-testid="newsletter-email-input"
          />
          <Button type="submit" className="btn-primary whitespace-nowrap" data-testid="newsletter-submit-btn">
            Subscribe
          </Button>
        </form>
        <p className="text-black/30 text-xs mt-4">
          We respect your privacy. Unsubscribe anytime.
        </p>
      </div>
    </section>
  );
};

// Footer Section
// Newsletter Signup Modal Component
const NewsletterModal = ({ isOpen, onClose }) => {
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState(null); // 'success', 'error', 'already_subscribed'

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!firstName.trim() || !email.trim()) return;
    
    setIsLoading(true);
    setStatus(null);
    
    try {
      const response = await fetch(`${API_URL}/api/newsletter/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ first_name: firstName.trim(), email: email.trim() })
      });
      
      const data = await response.json();
      
      if (data.status === "success") {
        setStatus("success");
        toast.success(`Welcome, ${firstName}! You're now subscribed.`);
        setTimeout(() => {
          onClose();
          setFirstName("");
          setEmail("");
          setStatus(null);
        }, 2000);
      } else if (data.status === "already_subscribed") {
        setStatus("already_subscribed");
        toast.info("You're already subscribed to our newsletter!");
      } else {
        throw new Error(data.message || "Subscription failed");
      }
    } catch (error) {
      console.error("Newsletter subscription error:", error);
      setStatus("error");
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="newsletter-modal-overlay"
    >
      <div 
        className="bg-[#F5F1E6] max-w-lg w-full rounded-sm shadow-2xl overflow-hidden animate-fade-in-up"
        onClick={(e) => e.stopPropagation()}
        data-testid="newsletter-modal"
      >
        {/* Modal Header */}
        <div className="relative bg-gradient-to-br from-[#1a1a1a] to-[#2d2d2d] px-8 py-10 text-center">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 text-white/60 hover:text-white transition-colors"
            aria-label="Close newsletter signup"
            data-testid="newsletter-modal-close"
          >
            <X className="w-5 h-5" />
          </button>
          
          <div className="mb-4">
            <img 
              src={LOGO_URL} 
              alt="Redefining Return" 
              className="h-16 w-auto mx-auto"
              style={{ filter: 'invert(1) brightness(0.9)', mixBlendMode: 'lighten' }}
            />
          </div>
          <h2 className="text-2xl md:text-3xl font-light text-white mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Join Our <em className="text-[#D4B06C]">Inner Circle</em>
          </h2>
          <p className="text-white/60 text-sm">
            Leadership insights, podcast updates, and exclusive content — delivered to your inbox.
          </p>
        </div>

        {/* Modal Body */}
        <div className="px-8 py-8">
          {status === "success" ? (
            <div className="text-center py-6" data-testid="newsletter-success">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#94A48A]/20 flex items-center justify-center">
                <Check className="w-8 h-8 text-[#94A48A]" />
              </div>
              <h3 className="text-xl font-light text-black/90 mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                You're In!
              </h3>
              <p className="text-black/50 text-sm">
                Welcome to the Redefining Return community.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-black/70 mb-2">First Name</label>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="Your first name"
                  className="w-full px-4 py-3 bg-white border border-black/10 rounded-sm text-black/80 placeholder:text-black/30 focus:outline-none focus:border-[#C27C62] transition-colors"
                  required
                  disabled={isLoading}
                  data-testid="newsletter-firstname-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-black/70 mb-2">Email Address</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="w-full px-4 py-3 bg-white border border-black/10 rounded-sm text-black/80 placeholder:text-black/30 focus:outline-none focus:border-[#C27C62] transition-colors"
                  required
                  disabled={isLoading}
                  data-testid="newsletter-email-input"
                />
              </div>
              
              {status === "error" && (
                <p className="text-red-500 text-sm">Something went wrong. Please try again.</p>
              )}
              {status === "already_subscribed" && (
                <p className="text-[#5A6F8C] text-sm">You're already part of our community!</p>
              )}
              
              <button
                type="submit"
                disabled={isLoading || !firstName.trim() || !email.trim()}
                className="w-full py-4 bg-[#C27C62] text-white font-medium rounded-sm hover:bg-[#a86a54] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                data-testid="newsletter-submit-btn"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Subscribing...
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4" />
                    Subscribe Now
                  </>
                )}
              </button>
              
              <p className="text-center text-black/40 text-xs mt-4">
                We respect your privacy. Unsubscribe anytime.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

const FooterSection = ({ onOpenNewsletterModal }) => {
  const [footerEmail, setFooterEmail] = useState("");
  const [footerName, setFooterName] = useState("");
  const [footerLoading, setFooterLoading] = useState(false);
  const [footerStatus, setFooterStatus] = useState(null);

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleFooterSubmit = async (e) => {
    e.preventDefault();
    if (!footerName.trim() || !footerEmail.trim()) return;
    
    setFooterLoading(true);
    setFooterStatus(null);
    
    try {
      const response = await fetch(`${API_URL}/api/newsletter/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ first_name: footerName.trim(), email: footerEmail.trim() })
      });
      
      const data = await response.json();
      
      if (data.status === "success") {
        setFooterStatus("success");
        toast.success(`Welcome, ${footerName}! You're now subscribed.`);
        setFooterName("");
        setFooterEmail("");
      } else if (data.status === "already_subscribed") {
        setFooterStatus("already_subscribed");
        toast.info("You're already subscribed!");
      } else {
        throw new Error(data.message);
      }
    } catch (error) {
      setFooterStatus("error");
      toast.error("Something went wrong. Please try again.");
    } finally {
      setFooterLoading(false);
    }
  };

  return (
    <footer className="footer-section" data-testid="footer-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Newsletter Signup Section */}
        <div className="border-b border-white/10 pb-10 mb-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-2xl md:text-3xl font-light text-white mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Stay <em className="text-[#D4B06C]">Connected</em>
              </h3>
              <p className="text-white/50 text-sm leading-relaxed">
                Leadership insights, podcast updates, and exclusive content delivered to your inbox.
              </p>
            </div>
            
            {footerStatus === "success" ? (
              <div className="flex items-center gap-3 text-[#94A48A]" data-testid="footer-newsletter-success">
                <Check className="w-5 h-5" />
                <span>You're subscribed! Welcome to the community.</span>
              </div>
            ) : (
              <form onSubmit={handleFooterSubmit} className="flex flex-col sm:flex-row gap-3" data-testid="footer-newsletter-form">
                <input
                  type="text"
                  value={footerName}
                  onChange={(e) => setFooterName(e.target.value)}
                  placeholder="First name"
                  className="flex-1 px-4 py-3 bg-white/10 border border-white/20 rounded-sm text-white placeholder:text-white/40 focus:outline-none focus:border-[#D4B06C] transition-colors"
                  required
                  disabled={footerLoading}
                  data-testid="footer-newsletter-name"
                />
                <input
                  type="email"
                  value={footerEmail}
                  onChange={(e) => setFooterEmail(e.target.value)}
                  placeholder="Email address"
                  className="flex-1 px-4 py-3 bg-white/10 border border-white/20 rounded-sm text-white placeholder:text-white/40 focus:outline-none focus:border-[#D4B06C] transition-colors"
                  required
                  disabled={footerLoading}
                  data-testid="footer-newsletter-email"
                />
                <button
                  type="submit"
                  disabled={footerLoading || !footerName.trim() || !footerEmail.trim()}
                  className="px-6 py-3 bg-[#C27C62] text-white font-medium rounded-sm hover:bg-[#a86a54] transition-colors disabled:opacity-50 flex items-center justify-center gap-2 whitespace-nowrap"
                  data-testid="footer-newsletter-submit"
                >
                  {footerLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Mail className="w-4 h-4" />
                      Subscribe
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-10">
          <div className="lg:col-span-2">
            <img 
              src={LOGO_URL} 
              alt="Redefining Return" 
              className="h-16 w-auto object-contain invert mb-6"
              style={{ mixBlendMode: 'lighten' }}
            />
            <p className="text-white/50 text-sm leading-relaxed max-w-md">
              Executive coaching for senior leaders architecting their leadership legacy. Strategic guidance from seasoned C-suite executives who've walked the path.
            </p>
          </div>

          <div>
            <h4 className="text-sm font-medium text-white mb-6 tracking-wide">Navigate</h4>
            <ul className="space-y-3">
              {['Home', 'Coaching', 'Approach', 'Coaches', 'Podcast'].map((link, i) => (
                <li key={i}>
                  <a
                    href={`#${['home', 'coaching', 'approach', 'team', 'podcast'][i]}`}
                    onClick={(e) => { e.preventDefault(); scrollToSection(`#${['home', 'coaching', 'approach', 'team', 'podcast'][i]}`); }}
                    className="text-white/40 text-sm hover:text-[#D4B06C] transition-colors"
                    data-testid={`footer-link-${i}`}
                  >
                    {link}
                  </a>
                </li>
              ))}
              <li>
                <a
                  href="/press"
                  className="text-white/40 text-sm hover:text-[#D4B06C] transition-colors"
                  data-testid="footer-link-press"
                >
                  Press Kit
                </a>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="text-sm font-medium text-white mb-6 tracking-wide">Connect</h4>
            <div className="flex gap-3 mb-6">
              <a href="https://www.instagram.com/redefiningreturn" target="_blank" rel="noopener noreferrer" className="social-icon" aria-label="Instagram" data-testid="social-instagram">
                <Instagram className="w-5 h-5" />
              </a>
              <a href="https://www.youtube.com/@RedefiningReturnPodcast" target="_blank" rel="noopener noreferrer" className="social-icon" aria-label="YouTube" data-testid="social-youtube">
                <Youtube className="w-5 h-5" />
              </a>
              <a href="https://www.tiktok.com/@redefiningreturnpodcast" target="_blank" rel="noopener noreferrer" className="social-icon" aria-label="TikTok" data-testid="social-tiktok">
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                </svg>
              </a>
            </div>
            <ul className="space-y-2">
              {['Privacy Policy', 'Terms of Service'].map((legal, i) => (
                <li key={i}>
                  <a href="#" className="text-white/30 text-xs hover:text-[#D4B06C] transition-colors" data-testid={`footer-legal-${i}`}>
                    {legal}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-white/30 text-sm">
            © 2025 Redefining Return. All rights reserved.
          </p>
          <p className="text-white/20 text-xs italic" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            The circle is never complete — transformation continues.
          </p>
        </div>
      </div>
    </footer>
  );
};

// Main Home Component
const Home = () => {
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [showNewsletterModal, setShowNewsletterModal] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);

      const sections = ['home', 'coaching', 'approach', 'team', 'podcast'];
      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= 100 && rect.bottom >= 100) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Show newsletter popup after 15 seconds (only once per session)
  useEffect(() => {
    const hasSeenPopup = sessionStorage.getItem('newsletter_popup_shown');
    if (!hasSeenPopup) {
      const timer = setTimeout(() => {
        setShowNewsletterModal(true);
        sessionStorage.setItem('newsletter_popup_shown', 'true');
      }, 15000); // 15 seconds
      return () => clearTimeout(timer);
    }
  }, []);

  return (
    <div className="min-h-screen bg-[#F5F1E6]">
      <div className="grain-overlay" />
      <Navigation scrolled={scrolled} activeSection={activeSection} />
      <HeroSection />
      <ServicesSection />
      <ApproachSection />
      <StatsSection />
      <LeadersServeSection />
      <TeamSection />
      <InsightsSection />
      <PodcastSection />
      <FooterSection onOpenNewsletterModal={() => setShowNewsletterModal(true)} />
      <NewsletterModal 
        isOpen={showNewsletterModal} 
        onClose={() => setShowNewsletterModal(false)} 
      />
      <Toaster position="top-right" />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/press" element={<PressPage />} />
          <Route path="/admin" element={<AdminDashboard />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
